// Copyright (c) 2023 MOCA Systems, Inc. All rights reserved.
// PLACEHOLDER REPLACE ENTIRE FILE AS PART OF TP-5368

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MaterialModule } from 'src/app/common/material/material-module';
import { PresentationModule } from 'src/app/common/presentation-components/presentation.module';
import { AccountCreationStore } from '../../services/account-creation-store.service';
import { map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import * as appStrings from '@touchplan/strings';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { LoggerServiceFactory } from 'src/app/common/logging/LoggerServiceFactory';
import { BannerMessageHandler } from '../../../logging/banner-message-handler';
import { PlatformConfiguration } from 'src/app/services/platform-configuration/platform-configuration.service';
import { TouchplanPlatform } from 'src/app/services/platform-configuration/touchplan-platforms.enum';
import { AuthOutputs } from '../../services/auth-outputs.service';
import { Meta, Title } from '@angular/platform-browser';
import { emailFormatValidator } from '../../../form-utils/validators';
import { AccountClaimsValidationService } from '../../services/account-claims-validation.service';

@Component({
	selector: 'app-login',
	standalone: true,
	imports: [CommonModule, RouterModule, MaterialModule, PresentationModule, ReactiveFormsModule],
	providers: [
		AccountClaimsValidationService,
		BannerMessageHandler,
		LoggerServiceFactory.provide('Auth Login Component'),
	],
	templateUrl: './login.component.html',
	styleUrls: ['./login.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent implements OnInit, OnDestroy {
	public destroy$ = new Subject<void>();
	protected appStrings = appStrings;

	protected isMobile: boolean;

	protected isSSO$ = this.accountCreationStore.isSSO$;

	protected isRallyRoute$: Observable<boolean>;

	public signUpForm = this.formBuilder.group({
		//TODO - replace the email validator with our standard one, wherever that is
		email: ['', [Validators.required, emailFormatValidator()]],
	});

	public save() {
		this.accountCreationStore.collectEmail(this.signUpForm.get('email').value);
		this.accountCreationStore.setLoadingState(true);

		this.accountCreationStore.email$
			.pipe(
				take(1),
				//check if the email is known
				switchMap((email) => this.accountClaimsValidationService.validateAccountClaimsFromEmail$(email)),
				tap({
					next: () => this.accountCreationStore.setLoadingState(false),
					error: () => {
						this.accountCreationStore.setLoadingState(false);
					},
				}),
				takeUntil(this.destroy$)
			)
			.subscribe({
				next: (t) => {
					this.authOutputs.finishLogin(t, this.activatedRoute);
				},
				error: (error) => {
					this.bannerMessageHandler.displayErrorMessage(error);
				},
			});
	}

	public ngOnInit(): void {
		this.isMobile = this.platform.platform === TouchplanPlatform.Mobile;
		this.isRallyRoute$ = this.activatedRoute.data.pipe(map((data) => !!data?.isRallyRoute));

		// Sets the title and meta description of the login page
		if (!this.isMobile) {
			this.titleService.setTitle(appStrings.META_DESKTOP_TITLE);
			this.metaService.addTag({ name: 'description', content: appStrings.META_DESKTOP_DESCRIPTION });
		} else {
			this.titleService.setTitle(appStrings.META_MOBILE_TITLE);
			this.metaService.addTag({ name: 'description', content: appStrings.META_MOBILE_DESCRIPTION });
		}

		this.accountCreationStore.email$.pipe(takeUntil(this.destroy$)).subscribe(
			(email) => this.signUpForm.patchValue({ email }, { emitEvent: false }),
			(error) => {
				this.bannerMessageHandler.displayErrorMessage(error);
			}
		);
	}

	public ngOnDestroy(): void {
		this.destroy$.next();
	}

	constructor(
		private accountCreationStore: AccountCreationStore,
		private formBuilder: FormBuilder,
		//...kinda silly this needs to be injected to do a relative redirect
		private activatedRoute: ActivatedRoute,
		private bannerMessageHandler: BannerMessageHandler,
		private platform: PlatformConfiguration,
		private authOutputs: AuthOutputs,
		private metaService: Meta,
		private titleService: Title,
		private accountClaimsValidationService: AccountClaimsValidationService
	) {}
}
